/**
 * ADR-225 §2 — the pure rules of influencer link capture (no DOM, no storage, no network).
 * Tested against the shared fixture design/influencers/attribution_link_cases.json
 * (scripts/tests/eros-attribution.test.mjs); the app, commerce-worker and the influencers
 * worker run the same fixture. eros-attribution.js (storage, touch) reads these from
 * window.ErosAttributionRules. Times are epoch milliseconds.
 */
(function (root) {
  'use strict';

  var PARAM = 'eros_ref';
  var STORE_KEY = 'eros_ref_v1';
  var TOUCH_KEY = 'eros_ref_touch_v1';
  var REF_RE = /^[a-z0-9-]{3,40}$/;
  var MAX_AGE_MS = 90 * 24 * 60 * 60 * 1000;

  /** A ref as the link carries it: trimmed and lower-cased, or null when it is not a valid slug. */
  function normaliseRef(raw) {
    if (typeof raw !== 'string') return null;
    var ref = raw.trim().toLowerCase();
    return REF_RE.test(ref) ? ref : null;
  }

  /** The query string of any address (https, or the app's custom scheme); the hash never counts. */
  function queryOf(url) {
    var s = String(url);
    var hash = s.indexOf('#');
    if (hash !== -1) s = s.slice(0, hash);
    var q = s.indexOf('?');
    return q === -1 ? '' : s.slice(q + 1);
  }

  /** The valid `eros_ref` of an address, else null. When the param repeats, the first one counts. */
  function parse(url) {
    if (typeof url !== 'string') return null;
    var all = new URLSearchParams(queryOf(url)).getAll(PARAM);
    return all.length ? normaliseRef(all[0]) : null;
  }

  /** {ref, at} when the value has that shape (the ref's format is judged later, by toSend), else null. */
  function shaped(v) {
    if (!v || typeof v.ref !== 'string' || typeof v.at !== 'number' || !isFinite(v.at)) return null;
    return { ref: v.ref, at: v.at };
  }

  /** The stored entry from its raw localStorage string; garbage reads as nothing. */
  function parseStored(raw) {
    if (typeof raw !== 'string' || !raw) return null;
    try {
      return shaped(JSON.parse(raw));
    } catch (e) {
      return null;
    }
  }

  /**
   * The entry to keep after a page load. A valid incoming ref always wins and takes the time of this
   * visit (the last link counts); an absent or invalid one never clears what is stored.
   */
  function nextStored(stored, incoming, now) {
    var ref = normaliseRef(incoming);
    if (ref) return { ref: ref, at: now };
    return shaped(stored);
  }

  /** What may leave the browser: {ref, at} aged 0..90 days with a well-formed ref, else null. */
  function toSend(stored, now) {
    var s = shaped(stored);
    if (!s || !REF_RE.test(s.ref)) return null;
    var age = now - s.at;
    return age >= 0 && age <= MAX_AGE_MS ? s : null;
  }

  /** The touch is sent once per (ref, at) for each signed-in customer: false when `sent` already says so. */
  function shouldTouch(current, sent, customer) {
    if (!current || !customer) return false;
    return !(sent && sent.ref === current.ref && sent.at === current.at && sent.customer === customer);
  }

  /** The record kept after a touch went out, read back by shouldTouch. */
  function touchRecord(current, customer) {
    return { ref: current.ref, at: current.at, customer: customer };
  }

  /** The sent-touch record from its raw localStorage string, or null. */
  function parseTouchRecord(raw) {
    if (typeof raw !== 'string' || !raw) return null;
    try {
      var v = JSON.parse(raw);
      var s = shaped(v);
      return s && typeof v.customer === 'string' ? { ref: s.ref, at: s.at, customer: v.customer } : null;
    } catch (e) {
      return null;
    }
  }

  var api = {
    PARAM: PARAM,
    STORE_KEY: STORE_KEY,
    TOUCH_KEY: TOUCH_KEY,
    MAX_AGE_MS: MAX_AGE_MS,
    parse: parse,
    parseStored: parseStored,
    nextStored: nextStored,
    toSend: toSend,
    shouldTouch: shouldTouch,
    touchRecord: touchRecord,
    parseTouchRecord: parseTouchRecord
  };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  else root.ErosAttributionRules = api;
})(typeof window !== 'undefined' ? window : globalThis);
